import axios, { AxiosInstance } from 'axios';
import ApiUrl from './config';
import { Gender } from './apiClient';

export interface WeighInProtocolEntryDto {
  id?: number;
  participantId?: number;
  lastName: string;
  firstName: string;
  patronymic?: string;
  birthDate: string;
  rank: string;
  weight: number;
  organization?: string;
  coachName?: string;
  sortOrder: number;
}

export interface WeighInProtocolModel {
  id: number;
  tournamentId: number;
  ageCategoryInTournamentId: number;
  weightCategoryInTournamentId: number;
  ageCategoryLabel?: string;
  weightCategoryLabel?: string;
  participantCategoryLabel?: string;
  weightCategoryKg?: string;
  protocolDate?: string;
  entries: WeighInProtocolEntryDto[];
}

export interface UpdateWeighInProtocolHeaderRequest {
  tournamentId: number;
  ageCategoryInTournamentId: number;
  weightCategoryInTournamentId: number;
  participantCategoryLabel: string;
  weightCategoryKg: string;
  protocolDate: string;
}

export interface WeighInProtocolStatus {
  ageCategoryInTournamentId: number;
  weightCategoryInTournamentId: number;
  ageCategoryLabel?: string;
  weightCategoryLabel?: string;
  genderLabel?: string;
  isCreated: boolean;
  entryCount: number;
  protocolId?: number;
  updatedOn?: string;
}

export interface QuickAddWeighInEntryRequest {
  tournamentId: number;
  ageCategoryInTournamentId: number;
  weightCategoryInTournamentId: number;
  lastName: string;
  firstName: string;
  patronymic?: string;
  birthDate: string;
  rank: string;
  weight: number;
  organization?: string;
  coachId?: number;
  coachName?: string;
  city?: string;
  gender: Gender;
}

export interface SaveWeighInProtocolRequest {
  tournamentId: number;
  ageCategoryInTournamentId: number;
  weightCategoryInTournamentId: number;
  entries: WeighInProtocolEntryDto[];
}

export class WeighInProtocolClient {
  private http: AxiosInstance;
  private baseUrl: string;

  constructor(baseUrl: string = ApiUrl, instance?: AxiosInstance) {
    // Общий axios: токен из AuthContext (axios.defaults.headers.common.Authorization).
    // Отдельный axios.create() токен не видит → 401 на Import/Save/QuickAdd.
    this.http = instance ?? axios;
    this.baseUrl = baseUrl ?? '';
  }

  private url(path: string) {
    return `${this.baseUrl}${path}`;
  }

  getProtocol(
    tournamentId: number,
    ageCategoryInTournamentId: number,
    weightCategoryInTournamentId: number
  ): Promise<WeighInProtocolModel> {
    return this.http
      .get<WeighInProtocolModel>(this.url('/api/WeighInProtocol/GetProtocol'), {
        params: { tournamentId, ageCategoryInTournamentId, weightCategoryInTournamentId },
      })
      .then((r) => r.data);
  }

  getTournamentStatuses(tournamentId: number): Promise<WeighInProtocolStatus[]> {
    return this.http
      .get<WeighInProtocolStatus[]>(this.url('/api/WeighInProtocol/GetTournamentStatuses'), {
        params: { tournamentId },
      })
      .then((r) => r.data);
  }

  importFromRegistered(
    tournamentId: number,
    ageCategoryInTournamentId: number,
    weightCategoryInTournamentId: number
  ): Promise<WeighInProtocolModel> {
    return this.http
      .post<WeighInProtocolModel>(this.url('/api/WeighInProtocol/ImportFromRegistered'), null, {
        params: { tournamentId, ageCategoryInTournamentId, weightCategoryInTournamentId },
      })
      .then((r) => r.data);
  }

  quickAddEntry(request: QuickAddWeighInEntryRequest): Promise<WeighInProtocolModel> {
    return this.http
      .post<WeighInProtocolModel>(this.url('/api/WeighInProtocol/QuickAddEntry'), request)
      .then((r) => r.data);
  }

  saveEntries(request: SaveWeighInProtocolRequest): Promise<WeighInProtocolModel> {
    return this.http
      .put<WeighInProtocolModel>(this.url('/api/WeighInProtocol/SaveEntries'), request)
      .then((r) => r.data);
  }

  removeEntry(entryId: number): Promise<WeighInProtocolModel> {
    return this.http
      .delete<WeighInProtocolModel>(this.url('/api/WeighInProtocol/RemoveEntry'), { params: { entryId } })
      .then((r) => r.data);
  }

  updateProtocolHeader(request: UpdateWeighInProtocolHeaderRequest): Promise<WeighInProtocolModel> {
    return this.http
      .put<WeighInProtocolModel>(this.url('/api/WeighInProtocol/UpdateProtocolHeader'), {
        ...request,
        protocolDate: request.protocolDate.includes('T')
          ? request.protocolDate
          : `${request.protocolDate}T00:00:00.000Z`,
      })
      .then((r) => r.data);
  }

  async printProtocol(
    tournamentId: number,
    ageCategoryInTournamentId: number,
    weightCategoryInTournamentId: number
  ): Promise<void> {
    const response = await this.http.get(this.url('/api/WeighInProtocol/PrintProtocol'), {
      params: { tournamentId, ageCategoryInTournamentId, weightCategoryInTournamentId },
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  }
}

export const weighInProtocolClient = new WeighInProtocolClient(ApiUrl, axios);
